import { useState, useRef, useCallback } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FloatingLabel } from './FloatingLabel';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ImageCaptureDialog from './ImageCaptureDialog';
import Icon from './Icon';
import { Validator } from './ValidationResult';
import DefaultButton from './DefaultButton';
import { useApp } from './AppProvider';
import UnavailableComponent from './UnavailableComponent';
import { withComponentCapabilities } from './capabilities/componentCapabilities';
import { materializeValue } from '../utils/lazyValue';

const fixedDefaultImage = 'data:image/png;base64,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';

export const toString = async ({ value, defaultValue }) => {
    value = await materializeValue(value);
    if (!value) {
        return defaultValue;
    }
    return '<image>';
}

/**
 * A simple control to display an image stored as a base64 encoded data URL and to accept the upload of images to be stored in this same format.
 * @component
 */
const ImageInput = withComponentCapabilities(({ width, height, placeholder, defaultImage, value, disabled = false, readonly = false, required = false, hideLabel = false, onChange, href }) => {
    const { t } = useTranslation();
    const { isApp } = useApp();
    const [error, setError] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [captureImage, setCaptureImage] = useState(false);
    const fileInputRef = useRef(null);
    const [validity, setValidity] = useState();
    const captureRef = useRef();

    const processFile = useCallback((file) => {
        const valid = fileInputRef.current.checkValidity();
        setValidity(valid);
        if (valid) {
            if (file?.type?.startsWith('image/') && file?.size <= 10 * 1024 * 1024) {
                const reader = new FileReader();
                reader.onload = (readEvent) => {
                    onChange(readEvent.target.result);
                    setError(false);
                };
                reader.readAsDataURL(file);
            } else if (file.size > 10 * 1024 * 1024) {
                setError(t('fileErrorSize', { maxSize: '10MB' }));
                setValidity(false);
            } else {
                setError(t('fileErrorImageOnly'));
                setValidity(false);
            }
        }

    }, [t, onChange]);

    const handleImageChange = useCallback((event) => {
        const file = event.target.files[0];
        processFile(file);
    }, [processFile]);

    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragOver(false);
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            const file = event.dataTransfer.files[0];
            processFile(file);
        }
    }, [processFile]);

    const handleClick = useCallback(() => {
        fileInputRef.current.click();
    }, []);

    const handleDragOver = useCallback((event) => {
        if (event.dataTransfer.items?.length === 1 && event.dataTransfer.items[0].kind === 'file') {
            event.preventDefault();
            event.stopPropagation();
            setIsDragOver(true);
        }
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const startCapture = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        if (isApp || !navigator.mediaDevices?.getUserMedia) {
            captureRef.current?.click(e);
        } else {
            setCaptureImage(true);
        }
    }, [isApp])

    const captureDone = useCallback(result => {
        if (result?.data) {
            const valid = fileInputRef.current.checkValidity();
            setValidity(valid);
            onChange(result.data);
            setError(false);
            setValidity(false);
        }
        setCaptureImage(false);
    }, [onChange]);

    const style = {
        ...(value ? {
            border: isDragOver ? '3px solid blue' : null
        } : {
            border: isDragOver ? '3px solid blue' : '3px dashed lightgrey',
            color: 'lightgrey',
            textAlign: 'center',
            cursor: 'pointer'
        }), ...({ objectFit: "contain", cursor: disabled ? null : 'cursor', width: width ?? '100%', maxHeight: height ?? '256px', objectPosition: 'center' })
    };
    style.position = 'relative'

    const isInvalid = (validity === false) || error;

    if (value === true) {
        return <UnavailableComponent placeholder={placeholder} text='unavailableComponentMessage'></UnavailableComponent>
    }

    if (disabled || readonly) {
        return <FloatingLabel placeholder={placeholder} value={true} hideLabel={hideLabel} href={href}>
            <img src={value ?? defaultImage ?? fixedDefaultImage} alt={placeholder} style={{ objectFit: "contain", maxWidth: width ?? '100%', maxHeight: height ?? '256px' }} />
        </FloatingLabel>
    }

    return (<>
        { captureImage && <ImageCaptureDialog onClose={captureDone}></ImageCaptureDialog> }
        <FloatingLabel placeholder={placeholder} value={true} hideLabel={hideLabel} href={href}>
            <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={handleClick}>
                <div style={{ position: 'absolute', left: '10px', top: '10px', zIndex: 5 }}>
                    <Button className="m-1" variant="default" onClick={startCapture}><Icon variant="solid" kind="camera"></Icon></Button>
                </div>
                { !required && value && <div style={{ position: 'absolute', right: '10px', bottom: '10px', zIndex: 5 }}>
                    <DefaultButton kind="delete" size="sm" hideLabel={true} onClick={e => { e.preventDefault(); e.stopPropagation(); onChange(null) }}></DefaultButton>
                </div> }
                <img src={value ?? defaultImage ?? fixedDefaultImage} alt={placeholder} style={style} />
            </div>
            <input ref={captureRef} type="file" accept="image/png,image/jpeg" capture="environment" style={{ display: 'none' }} />
            <Validator value={value} nonblockingError={error} ref={fileInputRef} onValidation={setValidity}>
                <Form.Control
                    type="file"
                    id="custom-file"
                    isInvalid={isInvalid}
                    label={t('fileUploadPrompt')}
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                    required={!value && required}
                    accept='image/png,image/jpeg'
                    />
            </Validator>
            </FloatingLabel>
        </>
    );
}, {
    lazyProps: ['value', 'defaultImage']
});

ImageInput.propTypes = {
    /**
     * A CSS width value to be used when rendering the image
     */
    width: PropTypes.string,

    /**
     * A CSS height value to be used when rendering the image
     */
    height: PropTypes.string,

    /**
     * A base64 encoded data URL representing the image to be shown when the value supplied is null / undefined
     */
    defaultImage: PropTypes.string,

    /**
     * The text to be displayed by the control
     */
    value: PropTypes.string,

    /**
     * Function to call when the list of selection values has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool
}

export default ImageInput;
